import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewExtraLarge, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';
import { inject } from 'mobx-react';

import { SwxlFooter } from 'components/common/footer/swxl-footer';
import { AdsenseContainer, DisplayAd } from 'constants/ads';
import { PageName } from 'constants/pages';
import { DisplayAdModel } from 'models/common/ads-types';
import { ExtendedContextStore } from 'types/flex-definitions';
import { MesoDisplayAd } from './meso-display-ad';
import { MesoTrigger } from './meso-trigger';
import { MesoAdsenseCsa, MesoCsaSlot } from './meso-adsense';

interface MesoFooterProps {
  destination: string;
  pageName: string;
  isDateless: boolean;
  context?: ExtendedContextStore;
}

const largeScreenAdsExperiment = 'PWA_CM1__AdSense_large_screens';
const sponsoredListingExperiment = 'Sponsored_Listing_PWA_Dateless';

@withLocalization
@inject('context')
export class MesoFooter extends React.Component<MesoFooterProps & LocalizedComponentProps> {
  public render() {
    const { l10n, pageName, destination, isDateless, context } = this.props;
    const searchQuery = l10n.formatText('meso.adsense.query', destination);

    return pageName === PageName.SEARCH ? (
      <HSRFooter searchQuery={searchQuery} context={context} />
    ) : (
      <HISFooter searchQuery={searchQuery} isDateless={isDateless} context={context} />
    );
  }
}

interface FooterProps {
  searchQuery: string;
  isDateless?: boolean;
  context?: ExtendedContextStore;
}

const showDisplayAdExp = (pageName: PageName) => (ads: DisplayAdModel[]) => (
  <DisplayAdExp ads={ads} experiment={largeScreenAdsExperiment} pageName={pageName} />
);

const showDisplayAdExpHSR = showDisplayAdExp(PageName.SEARCH);

const HSRFooter: React.FunctionComponent<FooterProps> = ({ searchQuery }) => (
  <div className="uitk-grid all-grid-align-center all-t-padding-six">
    <Viewport>
      <ViewMedium>{showDisplayAdExpHSR([DisplayAd.HSR_FOOTER_M_CM2, DisplayAd.HSR_FOOTER_M_CM1])}</ViewMedium>
      <ViewLarge>{showDisplayAdExpHSR([DisplayAd.HSR_FOOTER_L_CM2, DisplayAd.HSR_FOOTER_L_CM1])}</ViewLarge>
      <ViewExtraLarge>
        <React.Fragment>
          <div className="uitk-cell all-cell-1-1 all-b-padding-six">
            {showDisplayAdExpHSR([DisplayAd.HSR_FOOTER_XL_CM2, DisplayAd.HSR_FOOTER_XL_CM1])}
          </div>
          <div className="uitk-cell all-cell-1-1">
            <AdSenseExp container={AdsenseContainer.HSR_FOOTER} searchQuery={searchQuery} width="100%" />
          </div>
        </React.Fragment>
      </ViewExtraLarge>
    </Viewport>
  </div>
);

const showDisplayAdExpHIS = showDisplayAdExp(PageName.INFOSITE);

const HISFooter: React.FunctionComponent<FooterProps> = ({ searchQuery, isDateless, context }) => (
  <Viewport>
    <ViewSmall>
      <React.Fragment>
        <div className="uitk-grid all-grid-align-center all-y-padding-four all-x-padding-four">
          {isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four swxl" />}
          <div className="uitk-cell all-cell-1-1 all-b-padding-four">
            <div className="uitk-grid all-grid-align-center">
              <MesoDisplayAd uci={DisplayAd.HIS_FOOTER_S_CM6.uci} mediaquery={DisplayAd.HIS_FOOTER_S_CM6.mediaquery} />
            </div>
          </div>
          <MesoTrigger pageName={PageName.INFOSITE} />
          {isDateless && (
            <div className="uitk-cell all-cell-1-1">
              <AdSenseExp container={AdsenseContainer.HIS_FOOTER_1} searchQuery={searchQuery} width="100%" isMobile={true} />
            </div>
          )}
        </div>
      </React.Fragment>
    </ViewSmall>
    <ViewMedium>
      <React.Fragment>
        <div className="uitk-grid all-grid-align-center all-y-padding-four">
          {isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four" />}
          <div className="uitk-cell all-cell-1-1 all-b-padding-four">
            {showDisplayAdExpHIS([DisplayAd.HIS_FOOTER_M_CM3, DisplayAd.HIS_FOOTER_M_CM2])}
          </div>
          <div className="uitk-cell all-cell-1-1">
            <AdSenseExp container={AdsenseContainer.HIS_FOOTER_1} searchQuery={searchQuery} width="468px" />
          </div>
        </div>
      </React.Fragment>
    </ViewMedium>
    <ViewLarge>
      <React.Fragment>
        <div className="uitk-grid all-grid-align-center all-y-padding-four all-cell-1-2">
          <div className="uitk-cell all-cell-1-1 all-b-padding-four">{showDisplayAdExpHIS([DisplayAd.HIS_FOOTER_L_XL_CM1])}</div>
          {isDateless && <SwxlFooter context={context} className="uitk-cell all-r-padding-four uitk-flex-grow-1" />}
          <div className="uitk-cell uitk-flex-grow-1">
            <AdSenseExp container={AdsenseContainer.HIS_FOOTER_2} searchQuery={searchQuery} width="100%" />
          </div>
        </div>
      </React.Fragment>
    </ViewLarge>
  </Viewport>
);

interface AdSenseExpProps {
  container: string;
  searchQuery: string;
  width: string;
  isMobile?: boolean;
}

const AdSenseExp: React.FunctionComponent<AdSenseExpProps> = props => {
  const { searchQuery, ...restOfProps } = props;
  const experiment = !props.isMobile ? largeScreenAdsExperiment : sponsoredListingExperiment;

  return (
    <AbacusExperiment name={experiment}>
      <AbacusControl />
      <AbacusVariant bucket={1}>
        <div className={`uitk-grid all-grid-align-center`}>
          <MesoAdsenseCsa query={searchQuery}>
            <MesoCsaSlot type="content" {...restOfProps} />
          </MesoAdsenseCsa>
        </div>
      </AbacusVariant>
    </AbacusExperiment>
  );
};

interface DisplayAdExpProps {
  ads: DisplayAdModel[];
  experiment: string;
  pageName: string;
}

const DisplayAdExp: React.FunctionComponent<DisplayAdExpProps> = ({ ads, experiment, pageName }) => (
  <AbacusExperiment name={experiment}>
    <AbacusControl />
    <AbacusVariant bucket={1}>
      <React.Fragment>
        <section className="uitk-grid all-grid-align-center">
          {ads.map(({ mediaquery, uci }) => (
            <MesoDisplayAd key={uci} uci={uci} mediaquery={mediaquery} />
          ))}
        </section>
        <MesoTrigger pageName={pageName} />
      </React.Fragment>
    </AbacusVariant>
  </AbacusExperiment>
);
